import {
    TEmailAPIDeliverability,
    TEmailAPIDeliverabilityProviderReport,
    TEmailAPIDeliverabilityProviderReports,
    TEmailAPIDeliverabilityReports
} from "./types"
import { useContext, useEffect, useState } from "react"
import { EmailAPIContext } from "../context"
import { Stack } from "@mui/material"
import {
    EEmailProviders,
    EmailAPIFactory,
    EOperatorTypes,
    splitArray,
    TEmailReport
} from "@cakemail-org/ui-components-v2"
import { getAPIDateRange, getIntervalBasedOnTimeRange } from "../../../utils/date"
import {
    fillMissingReportIntervals,
    getProvidersToFetch,
    mergeArrayWithReduce,
    returnEmailTagsQuery,
    returnProviderQuery
} from "../../../utils/data"
import { emptyReportLog } from "../../../data/emailAPI"
import { KeyReputationMetrics } from "../components/analytics/keyReputationMetrics"
import { EmailSendVolume } from "../components/analytics/emailSendVolume"
import { TrendByProviders } from "../components/analytics/trendByProvider"


export function EmailAPIDeliverability({ }: TEmailAPIDeliverability) {
    const { timeRange, tagsFilter, providerFilter, filterOperator, refreshKey } = useContext(EmailAPIContext)

    const [loading, setLoading] = useState<boolean>(true)
    const [metrics, setMetrics] = useState<TEmailAPIDeliverabilityReports>()
    const [error, setError] = useState<boolean>(false)

    useEffect(fetchReports, [timeRange, tagsFilter, providerFilter, refreshKey])

    function getDefaultFetchParams() {
        const apiTimeRange = getAPIDateRange({ range: timeRange, rangeMultiplier: 2 })
        return {
            tags: returnEmailTagsQuery(tagsFilter, { operator: filterOperator }),
            startTime: apiTimeRange.start,
            endTime: apiTimeRange.end
        }
    }

    function fetchReports() {
        setLoading(true)
        const defaultParams = {
            interval: getIntervalBasedOnTimeRange(timeRange),
            ...getDefaultFetchParams()
        }

        const promisses: any[] = []

        const providersToFetch = getProvidersToFetch(providerFilter)

        providersToFetch.forEach((p: EEmailProviders) => {
            promisses.push(EmailAPIFactory.getReport({
                providers: returnProviderQuery({ providers: p, operator: providerFilter.length < 1 ? EOperatorTypes.or : filterOperator }),
                ...defaultParams
            }).then((data) => {
                return {
                    provider: p,
                    data
                }
            }))
        })
        Promise.allSettled(promisses).then((results) => {
            const newMetrics: TEmailAPIDeliverabilityReports = {}
            let hasOnlyErrors = true
            try {
                results.forEach((res) => {
                    if (res.status === "fulfilled") {
                        hasOnlyErrors = false
                        const entry: { provider: EEmailProviders, data: TEmailReport[] } = res.value
                        fillMissingReportIntervals(entry.data, defaultParams.startTime, defaultParams.endTime, defaultParams.interval, emptyReportLog)
                        newMetrics[entry.provider] = splitArray<TEmailReport>(entry.data, entry.data.length / 2)
                    }
                })
            } catch (e) {
                hasOnlyErrors = true
            }

            if (hasOnlyErrors) {
                setError(true)
            } else {
                setMetrics(newMetrics)
            }

            setLoading(false)
        })

    }

    let keyReputationMetric = undefined
    let emailSendVolumeMetric: TEmailAPIDeliverabilityProviderReport | undefined = undefined
    let trendByProviderMetrics: TEmailAPIDeliverabilityProviderReports | undefined = undefined

    if (metrics) {
        emailSendVolumeMetric = {}
        trendByProviderMetrics = {}
        //Fill a simplified key value for reports per provider
        Object.keys(metrics).forEach((provider) => {
            //@ts-ignore
            emailSendVolumeMetric[provider] = mergeArrayWithReduce<TEmailReport>(metrics[provider].secondArray)
            //@ts-ignore
            trendByProviderMetrics[provider] = metrics[provider].secondArray
        })

        //Merge Data from each propvider then merge providers togetter
        keyReputationMetric = mergeArrayWithReduce<TEmailReport>(
            Object.entries(emailSendVolumeMetric).map(([provider, data]) =>
                data
            )
        )
    }

    return <Stack className="emailAPIDeliverability" gap={8}>
        <KeyReputationMetrics
            timeRange={timeRange}
            tagsFilter={tagsFilter}
            providerFilter={providerFilter}
            filterOperator={filterOperator}
            refreshKey={refreshKey}
        />
        <EmailSendVolume
            controlled
            controlledProps={{
                metrics: emailSendVolumeMetric,
                fullMetrics: keyReputationMetric,
                loading,
                error
            }}
            timeRange={timeRange}
            tagsFilter={tagsFilter}
            providerFilter={providerFilter}
            filterOperator={filterOperator}
        />
        <TrendByProviders
            controlled
            controlledProps={{
                metrics: trendByProviderMetrics,
                loading,
                error
            }}
            timeRange={timeRange}
            tagsFilter={tagsFilter}
            providerFilter={providerFilter}
            filterOperator={filterOperator}
        />
    </Stack>
}